/**
 * SIDEBAR COUNTS PROVIDER
 *
 * This provider component wraps the MainLayout and provides dynamic sidebar counts
 * to all child components. It uses the useSidebarCounts hook to fetch real-time data
 * and updates the sidebar menu items accordingly.
 */

import React, { createContext, useContext, useMemo } from "react";
import { useSidebarCounts } from "../../hooks/useAdminStats";
import { createSidebarMenuItems, MenuItem, SidebarCounts, defaultSidebarMenuItems } from "../../modules/main/config/sidebarConfig";

// ===========================================
// CONTEXT DEFINITION
// ===========================================

interface SidebarCountsContextType {
  sidebarMenuItems: MenuItem[];
  counts: SidebarCounts;
  isLoading: boolean;
  refreshCounts: () => void;
}

const SidebarCountsContext = createContext<SidebarCountsContextType | undefined>(undefined);

// ===========================================
// PROVIDER COMPONENT
// ===========================================

interface SidebarCountsProviderProps {
  children: React.ReactNode;
  fallbackCounts?: Partial<SidebarCounts>;
  enableAutoRefresh?: boolean;
}

export const SidebarCountsProvider: React.FC<SidebarCountsProviderProps> = ({
  children,
  fallbackCounts,
  enableAutoRefresh = true,
}) => {
  // Fetch dynamic counts from API
  const {
    categoriesCount,
    imagesCount,
    videosCount,
    audiosCount,
    isLoading,
  } = useSidebarCounts();

  // Build complete counts object with fallbacks
  // ADM D8 — only counts something actually fetches. `usersCount`,
  // `subscriptionsCount` and `ticketsCount` used to be here with no API behind
  // them: 0 after load, and invented numbers from a constant while loading.
  const counts: SidebarCounts = useMemo(() => ({
    categoriesCount: categoriesCount || fallbackCounts?.categoriesCount || 0,
    imagesCount: imagesCount || fallbackCounts?.imagesCount || 0,
    videosCount: videosCount || fallbackCounts?.videosCount || 0,
    audiosCount: audiosCount || fallbackCounts?.audiosCount || 0,
  }), [
    categoriesCount,
    imagesCount,
    videosCount,
    audiosCount,
    fallbackCounts,
  ]);

  // Generate sidebar menu items with dynamic counts
  const sidebarMenuItems = useMemo(() => {
    if (isLoading) {
      // Return default items during loading to prevent layout shift
      return defaultSidebarMenuItems;
    }
    return createSidebarMenuItems(counts);
  }, [counts, isLoading]);

  // Refresh function (for manual refresh)
  const refreshCounts = () => {
    // The query will automatically refetch based on its configuration
    // This is mainly for manual refresh buttons if needed
    window.location.reload();
  };

  const contextValue: SidebarCountsContextType = {
    sidebarMenuItems,
    counts,
    isLoading,
    refreshCounts,
  };

  return (
    <SidebarCountsContext.Provider value={contextValue}>
      {children}
    </SidebarCountsContext.Provider>
  );
};

// ===========================================
// HOOKS FOR CONSUMING CONTEXT
// ===========================================

/**
 * Hook to access sidebar counts and menu items
 */
export const useSidebarCountsContext = (): SidebarCountsContextType => {
  const context = useContext(SidebarCountsContext);
  if (!context) {
    throw new Error(
      "useSidebarCountsContext must be used within a SidebarCountsProvider"
    );
  }
  return context;
};

/**
 * Hook to access only the sidebar menu items (most common use case)
 */
export const useDynamicSidebarItems = (): MenuItem[] => {
  const { sidebarMenuItems } = useSidebarCountsContext();
  return sidebarMenuItems;
};

/**
 * Hook to access specific count values
 */
export const useSpecificCounts = () => {
  const { counts } = useSidebarCountsContext();
  return counts;
};

// ===========================================
// HIGHER-ORDER COMPONENT (Alternative approach)
// ===========================================

/**
 * HOC that provides sidebar counts to wrapped component
 */
export const withSidebarCounts = <P extends object>(
  Component: React.ComponentType<P & { sidebarMenuItems: MenuItem[] }>
) => {
  return (props: P) => {
    const { sidebarMenuItems } = useSidebarCountsContext();
    return <Component {...props} sidebarMenuItems={sidebarMenuItems} />;
  };
};

// ===========================================
// USAGE EXAMPLES
// ===========================================

/**
 * Example 1: Wrap MainLayout with provider
 *
 * ```tsx
 * <SidebarCountsProvider>
 *   <MainLayout />
 * </SidebarCountsProvider>
 * ```
 *
 * Example 2: Use in MainLayout component
 *
 * ```tsx
 * const MainLayout = () => {
 *   const { sidebarMenuItems, isLoading } = useSidebarCountsContext();
 *
 *   return (
 *     <Flex>
 *       <Sidebar>
 *         {sidebarMenuItems.map(item => (
 *           <SidebarMenuItem key={item.id} item={item} />
 *         ))}
 *       </Sidebar>
 *     </Flex>
 *   );
 * };
 * ```
 *
 * Example 3: Use specific counts in components
 *
 * ```tsx
 * const CategoryBadge = () => {
 *   const { categoriesCount } = useSpecificCounts();
 *   return <Badge>{categoriesCount}</Badge>;
 * };
 * ```
 */

export default SidebarCountsProvider;
